import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ChevronDown, CircleDashed, CircleMinus, Lock, PlugZap, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ApiError } from "@hostdock/sdk";
import type { MigrationCapability, MigrationConnectionTest, MigrationProviderInfo, MigrationSupportLevel } from "@hostdock/types";
import { Badge, Button, Checkbox, cn, Dialog, Field, FormError, Input, KeyValue, Notice, Select } from "@hostdock/ui";
import { api } from "../api";

/**
 * The control-panel side of the migration wizard. Everything panel-specific (address examples, sign-in methods, what
 * can be moved) comes from the selected adapter through the API; nothing here assumes cPanel. Ports and protocols are
 * worked out by the API from the login address, so the customer is never asked for them unless they open "Advanced".
 */

/** The order sources are offered in. Generic SSH/FTP/archive sources and the WordPress SFTP route are not listed. */
export const SOURCE_ORDER = ["cpanel", "directadmin", "plesk", "webuzo", "cyberpanel", "ispconfig", "enhance", "wordpress-admin"];
export const OTHER = "other";

const SUPPORT: Record<MigrationSupportLevel, { label: string; tone: "success" | "info" | "neutral" }> = {
  SUPPORTED: { label: "Supported", tone: "success" },
  PARTIALLY_SUPPORTED: { label: "Partial", tone: "info" },
  NOT_IMPLEMENTED: { label: "Not available yet", tone: "neutral" },
};

export function SupportBadge({ level }: { level: MigrationSupportLevel }) {
  return <Badge tone={SUPPORT[level].tone}>{SUPPORT[level].label}</Badge>;
}

function sourceSummary(p: MigrationProviderInfo): string {
  if (p.connection.kind === "website") return "Website address and admin login, from any host";
  if (p.support === "SUPPORTED") return "Full account migration";
  if (p.support === "PARTIALLY_SUPPORTED") return "Moves some items; the rest are listed for you";
  return "Recognised, but HostDock cannot migrate from it yet";
}

/** Step 1: "Where is your website currently hosted?" Each card states honestly how well HostDock supports it. */
export function SourcePicker({ providers, value, onPick }: { providers: MigrationProviderInfo[]; value: string; onPick(id: string): void }) {
  const listed = SOURCE_ORDER.map((id) => providers.find((p) => p.id === id)).filter((p): p is MigrationProviderInfo => Boolean(p));
  const card = (id: string, label: string, sub: string, badge: React.ReactNode) => {
    const chosen = value === id;
    return (
      <button key={id} type="button" aria-pressed={chosen} onClick={() => onPick(id)}
        className={cn("flex w-full items-center gap-3 rounded-xl border px-[14px] py-[12px] text-left transition-colors",
          chosen ? "border-[#048d9f] bg-[#effafb] ring-1 ring-[#048d9f] dark:bg-accent-soft" : "border-line bg-surface hover:border-line-strong")}>
        <span className={cn("flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl text-[13px] font-bold", chosen ? "bg-[#048d9f] text-white" : "bg-surface-3 text-fg/80")} aria-hidden>
          {label.replace(/[^A-Za-z]/g, "").slice(0, 2)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] font-semibold">{label}{badge}</span>
          <span className="block text-[12px] text-muted">{sub}</span>
        </span>
        {chosen && <CheckCircle2 size={18} className="shrink-0 text-[#048d9f]" />}
      </button>
    );
  };
  return (
    <div className="grid gap-2.5 sm:grid-cols-2">
      {listed.map((p) => card(p.id, p.label, sourceSummary(p), <SupportBadge level={p.support} />))}
      {card(OTHER, "Other / Manual migration", "A host or panel that is not listed here", null)}
    </div>
  );
}

const CAP_ICON: Record<MigrationSupportLevel, { icon: typeof CheckCircle2; className: string; label: string }> = {
  SUPPORTED: { icon: CheckCircle2, className: "text-success", label: "Migrated" },
  PARTIALLY_SUPPORTED: { icon: CircleDashed, className: "text-info", label: "Partly" },
  NOT_IMPLEMENTED: { icon: CircleMinus, className: "text-subtle", label: "Not migrated" },
};

/** What this adapter can really move, as the backend declares it. */
export function CapabilityList({ caps, title = "What HostDock can move from here" }: { caps: MigrationCapability[]; title?: string }) {
  if (!caps.length) return null;
  return (
    <div>
      <p className="mb-1.5 text-[12.5px] font-semibold text-fg">{title}</p>
      <ul className="grid gap-x-4 gap-y-1 sm:grid-cols-2" aria-label={title}>
        {caps.map((c) => {
          const s = CAP_ICON[c.status];
          return (
            <li key={c.item} className="flex items-start gap-1.5 text-[12.5px]" title={c.note}>
              <s.icon size={14} className={cn("mt-[2px] shrink-0", s.className)} aria-hidden />
              <span className={cn("min-w-0", c.status === "NOT_IMPLEMENTED" && "text-muted")}>{c.item}<span className="sr-only">: {s.label}</span>{c.note && c.status !== "SUPPORTED" && <span className="block text-[11.5px] text-subtle">{c.note}</span>}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

type CertReview = { fingerprint256: string; subject: string | null; issuer: string | null; validTo: string | null; changed?: boolean };
type Mismatch = { detected: string; detectedLabel: string };

/**
 * "Connect your <Panel> account": the panel login address, the sign-in for the method the adapter offers, and Test
 * connection. Continue creates the migration with exactly what the test found (port, approved certificate, method).
 */
export function PanelConnect({ provider, address, onAddress, onSwitch, onBack }: {
  provider: MigrationProviderInfo;
  address: string;
  onAddress(v: string): void;
  /** Another panel was detected at this address and the customer chose to use it. */
  onSwitch(id: string): void;
  onBack(): void;
}) {
  const navigate = useNavigate();
  const [methodId, setMethodId] = useState(provider.authMethods[0]?.id ?? "password");
  const method = provider.authMethods.find((a) => a.id === methodId) ?? provider.authMethods[0]!;
  const [creds, setCreds] = useState({ username: "", secret: "" });
  const [showSecret, setShowSecret] = useState(false);
  const [port, setPort] = useState("");
  const [review, setReview] = useState<CertReview | null>(null);
  const [trusted, setTrusted] = useState(false);
  const [approved, setApproved] = useState<string | null>(null);

  const test = useMutation({
    mutationFn: (fp: string | null) => api.migrations.testConnection({
      provider: provider.id as never, address: address.trim(), port: port ? Number(port) : undefined, tlsFingerprint: fp ?? undefined,
      authMethod: method.id, username: creds.username.trim(), secret: creds.secret,
    }),
    onError: (err) => {
      if (err instanceof ApiError && (err.code === "TLS_APPROVAL_REQUIRED" || err.code === "TLS_FINGERPRINT_CHANGED")) {
        setTrusted(false);
        setReview({ ...(err.details as CertReview), changed: err.code === "TLS_FINGERPRINT_CHANGED" });
      }
    },
  });
  const result: MigrationConnectionTest | undefined = test.data;
  const create = useMutation({
    mutationFn: (r: MigrationConnectionTest) => api.migrations.create({
      provider: provider.id as never, host: address.trim(), port: r.port, tlsFingerprint: r.tlsFingerprint, authMethod: method.id,
      username: creds.username.trim(), secret: creds.secret,
    }),
    onSuccess: (m) => { setCreds({ username: "", secret: "" }); navigate(`/migrate/${m.id}`); },
    onError: (err) => {
      // The server's certificate changed between the test and now: review it again, nothing was stored.
      if (err instanceof ApiError && (err.code === "TLS_FINGERPRINT_CHANGED" || err.code === "TLS_APPROVAL_REQUIRED")) { test.reset(); setTrusted(false); setReview({ ...(err.details as CertReview), changed: true }); }
    },
  });
  // Anything the test depended on changed: the result no longer applies.
  const edit = (fn: () => void) => { fn(); if (test.data || test.error) test.reset(); create.reset(); };

  const err = test.error instanceof ApiError ? test.error : null;
  const mismatch = err?.code === "PANEL_MISMATCH" ? (err.details as Mismatch) : null;
  const silent = err && ["TLS_APPROVAL_REQUIRED", "TLS_FINGERPRINT_CHANGED", "PANEL_MISMATCH"].includes(err.code);
  const ready = address.trim().length > 2 && method.fields.filter((f) => f.required).every((f) => creds[f.name as "username" | "secret"]?.trim());
  const secretField = method.fields.find((f) => f.name === "secret");

  return (
    <form className="flex flex-col gap-4" autoComplete="off" onSubmit={(e) => { e.preventDefault(); if (result) create.mutate(result); else if (ready) test.mutate(approved); }}>
      <Field label="Panel login address" hint={<>The address you open {provider.label} at. Any of these work: {provider.connection.addressExamples.map((x, i) => <span key={x}>{i > 0 && ", "}<span className="font-mono text-[11.5px]">{x}</span></span>)}</>}>
        {(p) => <Input {...p} value={address} onChange={(e) => edit(() => { onAddress(e.target.value); setApproved(null); })} placeholder={provider.connection.addressExamples[0]} autoFocus autoComplete="off" spellCheck={false} inputMode="url" />}
      </Field>
      {method.hint && <p className="-mt-1 text-[12.5px] text-muted">{method.hint}</p>}
      {method.fields.map((f) => (
        <Field key={`${method.id}-${f.name}`} label={f.label} hint={f.hint}>
          {(p) => <Input {...p} type={f.type === "password" && !showSecret ? "password" : "text"} value={creds[f.name as "username" | "secret"]} onChange={(e) => edit(() => setCreds({ ...creds, [f.name]: e.target.value }))} spellCheck={false} autoComplete={f.type === "password" ? "new-password" : "off"} />}
        </Field>
      ))}
      {secretField && <Checkbox checked={showSecret} onChange={setShowSecret} label={secretField.label === "Password" ? "Show password" : `Show ${secretField.label}`} />}

      <details className="group rounded-xl border border-line px-[14px] py-[10px]">
        <summary className="flex cursor-pointer list-none items-center justify-between text-[13px] font-medium text-fg">Advanced settings<ChevronDown size={15} className="text-muted transition-transform group-open:rotate-180" /></summary>
        <div className="mt-3 flex flex-col gap-3">
          {provider.authMethods.length > 1 && (
            <Field label="Sign-in method" hint="Username and password is selected. Use the other method if your account has two-factor sign-in.">
              {(p) => <Select {...p} value={method.id} onChange={(e) => edit(() => { setMethodId(e.target.value); setCreds((c) => ({ ...c, secret: "" })); })}>{provider.authMethods.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}</Select>}
            </Field>
          )}
          <Field label="Port" hint={`Leave empty: HostDock finds ${provider.label}'s encrypted port itself (from the address, then ${provider.defaultPorts.join(", ")}).`}>
            {(p) => <Input {...p} inputMode="numeric" value={port} onChange={(e) => edit(() => setPort(e.target.value.replace(/\D/g, "").slice(0, 5)))} placeholder="Detect automatically" className="max-w-[12rem]" />}
          </Field>
          <p className="text-[12.5px] text-muted"><span className="font-medium text-fg">Protocol:</span> HTTPS only. HostDock never sends your sign-in over an unencrypted connection.</p>
          <p className="text-[12.5px] text-muted">
            <span className="font-medium text-fg">SSL/TLS:</span>{" "}
            {result ? (result.tlsFingerprint ? <>certificate not publicly trusted; you approved fingerprint <span className="break-all font-mono text-[11.5px]">{result.tlsFingerprint}</span> for this migration.</> : "publicly trusted certificate, verified.")
              : "the certificate is always checked. If it is not publicly trusted, you are shown it to review; verification is never turned off."}
          </p>
        </div>
      </details>

      {mismatch && (
        <Notice tone="warning" title="Different control panel detected" action={<Button variant="primary" onClick={() => onSwitch(mismatch.detected)}>Use {mismatch.detectedLabel}</Button>}>
          You selected {provider.label}, but this server appears to be running {mismatch.detectedLabel} instead of {provider.label}.
        </Notice>
      )}
      {test.error && !silent && <FormError error={test.error} />}
      {result && <ConnectedCard r={result} />}
      {create.error && !(create.error instanceof ApiError && ["TLS_FINGERPRINT_CHANGED", "TLS_APPROVAL_REQUIRED"].includes(create.error.code)) && <FormError error={create.error} />}

      <div className="flex justify-between border-t border-line pt-4">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        {result
          ? <Button variant="primary" type="submit" loading={create.isPending}>Continue</Button>
          : <Button variant="primary" type="submit" loading={test.isPending} disabled={!ready} icon={<PlugZap size={14} />}>Test connection</Button>}
      </div>

      <Dialog
        open={review !== null}
        onOpenChange={(o) => { if (!o) setReview(null); }}
        title="SSL certificate problem"
        description={review?.changed
          ? "The server's certificate changed since you reviewed it. Check the new fingerprint with your host before trusting it."
          : `HostDock reached the server, but its certificate is not issued by a public authority (common for control panels on their own port), so it could not be validated. Your password has not been sent. Check the fingerprint against what your host shows before trusting it.`}
        footer={<><Button variant="ghost" onClick={() => setReview(null)}>Cancel</Button><Button variant="primary" disabled={!trusted} loading={test.isPending} onClick={() => { const fp = review!.fingerprint256; setApproved(fp); setReview(null); test.mutate(fp); }}>Trust and test</Button></>}
      >
        {review && (
          <div className="flex flex-col gap-4">
            <KeyValue items={[{ label: "Issued to", value: review.subject ?? "—" }, { label: "Issued by", value: review.issuer ?? "—" }, { label: "Valid until", value: review.validTo ?? "—" }, { label: "SHA-256 fingerprint", value: <span className="break-all font-mono text-[12px]">{review.fingerprint256}</span> }]} />
            <Checkbox checked={trusted} onChange={setTrusted} label="I recognise this server and trust this certificate for this migration only" />
          </div>
        )}
      </Dialog>
    </form>
  );
}

function ConnectedCard({ r }: { r: MigrationConnectionTest }) {
  return (
    <div className="rounded-xl border border-[#9fdcc0] bg-[#effaf4] px-[16px] py-[14px] dark:border-success/40 dark:bg-success-soft" aria-live="polite">
      <p className="flex items-center gap-2 text-[14px] font-bold text-[#14804a] dark:text-success"><CheckCircle2 size={17} />Connected successfully</p>
      <div className="mt-2"><KeyValue items={[
        { label: "Control panel", value: `${r.providerLabel} detected${r.account.panelVersion ? ` (${r.account.panelVersion})` : ""}` },
        { label: "Account", value: r.account.username },
        { label: "Domain", value: r.account.primaryDomain ?? "—" },
        { label: "Server", value: `${r.host}:${r.port}` },
      ]} /></div>
      {r.note && <p className="mt-2 text-[12.5px] text-muted">{r.note}</p>}
      <div className="mt-3 border-t border-[#c9ecd9] pt-3 dark:border-line"><CapabilityList caps={r.capabilities} /></div>
      <p className="mt-3 flex items-center gap-1.5 text-[12px] text-muted"><Lock size={12} />Continue reads your whole account; you choose what to migrate next.</p>
    </div>
  );
}

/** A panel HostDock recognises but has no working adapter for: say so, and offer only routes that really work. */
export function UnavailableSource({ provider, onPick, onBack }: { provider: MigrationProviderInfo; onPick(id: string): void; onBack(): void }) {
  return (
    <div className="flex flex-col gap-4">
      <Notice tone="warning" title={`Migration from ${provider.label} is not available yet`}>
        HostDock can recognise a {provider.label} server, but it cannot sign in to {provider.label} or copy an account from it yet, so it does not ask for your password.
      </Notice>
      <CapabilityList caps={provider.capabilities} title={`${provider.label} support today`} />
      <ManualRoutes onPick={onPick} />
      <div className="flex justify-start border-t border-line pt-4"><Button variant="ghost" onClick={onBack}>Back</Button></div>
    </div>
  );
}

/** "Other / Manual migration": the routes that work from any host. */
export function OtherSource({ onPick, onBack }: { onPick(id: string): void; onBack(): void }) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-[12.5px] text-muted">HostDock signs in to cPanel, DirectAdmin, Plesk and Webuzo directly. For any other host, these routes work:</p>
      <ManualRoutes onPick={onPick} />
      <div className="flex justify-start border-t border-line pt-4"><Button variant="ghost" onClick={onBack}>Back</Button></div>
    </div>
  );
}

function ManualRoutes({ onPick }: { onPick(id: string): void }) {
  return (
    <ul className="flex flex-col gap-2.5">
      <li className="flex flex-col gap-2 rounded-xl border border-line p-3.5 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold">A WordPress website</p>
          <p className="text-[12.5px] text-muted">Moved automatically from any host with its address and an administrator login: files, database and PHP version.</p>
        </div>
        <Button variant="primary" onClick={() => onPick("wordpress-admin")}>Migrate a WordPress site</Button>
      </li>
      <li className="flex flex-col gap-2 rounded-xl border border-line p-3.5 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold">Any other website, by hand</p>
          <p className="text-[12.5px] text-muted">Create the website on HostDock and upload its files with the file manager or SFTP; databases, mailboxes and DNS records are set up under Databases, Email and DNS. HostDock does not copy these for you on this route.</p>
        </div>
        <Link to="/websites/new" className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-line px-3 text-[13px] font-medium hover:border-line-strong">Create a website</Link>
      </li>
      <li className="flex items-start gap-2 text-[12px] text-subtle"><AlertTriangle size={13} className="mt-[2px] shrink-0" />HostDock only moves what it can read reliably; nothing is guessed or half-copied.</li>
      <li className="flex items-start gap-2 text-[12px] text-subtle"><ShieldCheck size={13} className="mt-[2px] shrink-0" />Your old host is never changed by a migration.</li>
    </ul>
  );
}
